import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  Image,
  Modal,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconEye,
  IconFile,
  IconFileText,
  IconFolderOpen,
  IconPdf,
  IconPencil,
  IconPhoto,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { deleteOne, fetchList, updateOne } from '@/shared/api/jsonapi/client';
import { uploadMediaFile } from '@/shared/lib/mediaUpload';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { notifications } from '@mantine/notifications';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface MediaFileRow extends Denormalized {
  type: 'media-files';
  id: string;
  fileName?: string;
  contentType?: string;
  size?: number;
  thumbUrl?: string;
  url?: string;
  createdAt?: string;
}

export const Route = createFileRoute('/_app/media')({ component: MediaPage });

type Kind = 'all' | 'image' | 'pdf' | 'doc' | 'other';

function kindOf(ct?: string, name?: string): Kind {
  const t = (ct ?? '').toLowerCase();
  const n = (name ?? '').toLowerCase();
  if (t.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(n)) return 'image';
  if (t === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (/\.(docx?|xlsx?|pptx?|txt|csv|md)$/i.test(n)) return 'doc';
  return 'other';
}

function iconFor(k: Kind): typeof IconFile {
  if (k === 'image') return IconPhoto;
  if (k === 'pdf') return IconPdf;
  if (k === 'doc') return IconFileText;
  return IconFile;
}

function fmtSize(bytes?: number): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

async function uploadOne(file: File): Promise<string> {
  return uploadMediaFile(file);
}

function MediaPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Kind>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<MediaFileRow | null>(null);
  const [renameTarget, setRenameTarget] = useState<MediaFileRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameOpened, renameCtrl] = useDisclosure(false);

  const listQ = useQuery({
    queryKey: ['media-files'],
    queryFn: () => fetchList<MediaFileRow>('media-files', {
      sort: ['-createdAt', '-id'],
      page: { size: 200 },
    }),
  });

  const items = useMemo(() => listQ.data?.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return items.filter((m) => {
      const k = kindOf(m.contentType, m.fileName);
      if (filter !== 'all' && k !== filter) return false;
      if (q && !(m.fileName ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filter, debouncedSearch]);

  const counts = useMemo(() => ({
    all: items.length,
    image: items.filter((m) => kindOf(m.contentType, m.fileName) === 'image').length,
    pdf: items.filter((m) => kindOf(m.contentType, m.fileName) === 'pdf').length,
    doc: items.filter((m) => kindOf(m.contentType, m.fileName) === 'doc').length,
    other: items.filter((m) => kindOf(m.contentType, m.fileName) === 'other').length,
  }), [items]);

  const totalBytes = useMemo(() => items.reduce((s, m) => s + (m.size ?? 0), 0), [items]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadOne(f);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-files'] });
      notifySuccess('Upload completato');
    },
    onError: (err) => notifyError(err, 'Upload fallito'),
  });

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => deleteOne('media-files', id))),
    onSuccess: (_, ids) => {
      qc.invalidateQueries({ queryKey: ['media-files'] });
      setSelected(new Set());
      notifySuccess(`${ids.length} file rimoss${ids.length === 1 ? 'o' : 'i'}`);
    },
    onError: (err) => notifyError(err, 'Rimozione fallita'),
  });

  const rename = useMutation({
    mutationFn: () => updateOne('media-files', {
      id: renameTarget!.id,
      attributes: { fileName: renameValue.trim() },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-files'] });
      notifySuccess('File rinominato');
      renameCtrl.close();
      setRenameTarget(null);
    },
    onError: (err) => notifyError(err, 'Rinomina fallita'),
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const askDeleteSelected = () => {
    if (selected.size === 0) return;
    modals.openConfirmModal({
      title: 'Eliminare file selezionati?',
      children: <Text size="sm">Stai per eliminare definitivamente <strong>{selected.size}</strong> file. L'operazione non è reversibile.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(Array.from(selected)),
    });
  };

  const askDeleteSingle = (m: MediaFileRow) => {
    modals.openConfirmModal({
      title: 'Eliminare questo file?',
      children: <Text size="sm">"{m.fileName ?? m.id}" verrà eliminato definitivamente.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate([m.id]),
    });
  };

  const copyUrl = async (m: MediaFileRow) => {
    const url = m.url ?? m.thumbUrl ?? '';
    if (!url) {
      notifications.show({ color: 'orange', message: 'URL non disponibile' });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      notifications.show({ color: 'green', message: 'URL copiato' });
    } catch {
      notifications.show({ color: 'red', message: 'Impossibile copiare' });
    }
  };

  const openRename = (m: MediaFileRow) => {
    setRenameTarget(m);
    setRenameValue(m.fileName ?? '');
    renameCtrl.open();
  };

  return (
    <Stack>
      <PageHeader
        title="Media library"
        description={`${items.length} file · ${fmtSize(totalBytes)} totali`}
      />

      <Dropzone
        onDrop={(files) => void upload.mutate(files)}
        onReject={() => notifyError('File rifiutato')}
        maxSize={200 * 1024 ** 2}
        accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
        loading={upload.isPending}
        multiple
      >
        <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={48} color="var(--mantine-color-blue-6)" />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={48} color="var(--mantine-color-red-6)" />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconUpload size={48} color="var(--mantine-color-dimmed)" />
          </Dropzone.Idle>
          <div>
            <Text size="lg">Trascina file o clicca per caricare</Text>
            <Text size="sm" c="dimmed" mt={4}>Immagini e PDF fino a 200 MB · selezione multipla</Text>
          </div>
        </Group>
      </Dropzone>

      <Group justify="space-between" wrap="wrap">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Cerca per nome…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={240}
          />
          <SegmentedControl
            value={filter}
            onChange={(v) => setFilter(v as Kind)}
            data={[
              { value: 'all', label: `Tutti (${counts.all})` },
              { value: 'image', label: `Immagini (${counts.image})` },
              { value: 'pdf', label: `PDF (${counts.pdf})` },
              { value: 'doc', label: `Doc (${counts.doc})` },
              { value: 'other', label: `Altro (${counts.other})` },
            ]}
            size="xs"
          />
        </Group>
        {selected.size > 0 && (
          <Group gap="xs">
            <Badge color="blue" variant="filled">{selected.size} selezionat{selected.size === 1 ? 'o' : 'i'}</Badge>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setSelected(new Set())}
            >
              Deseleziona
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={<IconTrash size={14} />}
              onClick={askDeleteSelected}
              loading={remove.isPending}
            >
              Elimina selezionati
            </Button>
          </Group>
        )}
      </Group>

      {listQ.isError ? (
        <QueryErrorAlert error={listQ.error} onRetry={() => void listQ.refetch()} />
      ) : listQ.isLoading ? (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} withBorder padding="xs" radius="md">
              <Card.Section>
                <div style={{ aspectRatio: 1, background: 'var(--mantine-color-default-hover)' }} />
              </Card.Section>
              <div style={{ height: 32, marginTop: 8 }} />
            </Card>
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <Paper withBorder radius="md">
          <EmptyState
            icon={IconFolderOpen}
            title={items.length === 0 ? 'Nessun file caricato' : 'Nessun file corrisponde ai filtri'}
            description={items.length === 0
              ? 'Trascina file nel dropzone sopra per iniziare.'
              : 'Modifica filtri o cerca con altri termini.'}
          />
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }}>
          {filtered.map((m) => (
            <MediaCard
              key={m.id}
              file={m}
              selected={selected.has(m.id)}
              onToggleSelect={() => toggleSelect(m.id)}
              onPreview={() => setPreview(m)}
              onCopyUrl={() => void copyUrl(m)}
              onRename={() => openRename(m)}
              onDelete={() => askDeleteSingle(m)}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Preview modal */}
      <Modal
        opened={preview != null}
        onClose={() => setPreview(null)}
        title={preview?.fileName ?? 'Anteprima'}
        size="xl"
        centered
      >
        {preview && (
          <Stack>
            {kindOf(preview.contentType, preview.fileName) === 'image' ? (
              <Image src={preview.url ?? preview.thumbUrl} alt={preview.fileName ?? ''} fit="contain" mah={600} />
            ) : kindOf(preview.contentType, preview.fileName) === 'pdf' && preview.url ? (
              <iframe
                src={preview.url}
                title={preview.fileName ?? ''}
                style={{ width: '100%', height: 600, border: 'none', borderRadius: 8 }}
              />
            ) : (
              <Center py="xl">
                <Stack align="center">
                  <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                    <IconFile size={32} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed">Anteprima non disponibile per questo tipo di file.</Text>
                </Stack>
              </Center>
            )}
            <Group justify="space-between">
              <Stack gap={0}>
                <Text size="sm" fw={500}>{preview.fileName}</Text>
                <Text size="xs" c="dimmed">
                  {preview.contentType ?? 'sconosciuto'} · {fmtSize(preview.size)}
                  {preview.createdAt && ` · ${dayjs(preview.createdAt).format('DD/MM/YYYY HH:mm')}`}
                </Text>
              </Stack>
              <Group gap="xs">
                {preview.url && (
                  <Button
                    component="a"
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    leftSection={<IconDownload size={14} />}
                    size="sm"
                  >
                    Scarica
                  </Button>
                )}
                <Button
                  variant="subtle"
                  leftSection={<IconCopy size={14} />}
                  size="sm"
                  onClick={() => void copyUrl(preview)}
                >
                  Copia URL
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Rename modal */}
      <Modal
        opened={renameOpened}
        onClose={() => { renameCtrl.close(); setRenameTarget(null); }}
        title="Rinomina file"
        centered
      >
        <Stack>
          <TextInput
            label="Nome file"
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { renameCtrl.close(); setRenameTarget(null); }}>Annulla</Button>
            <Button
              leftSection={<IconCheck size={14} />}
              disabled={!renameValue.trim() || renameValue === renameTarget?.fileName}
              loading={rename.isPending}
              onClick={() => rename.mutate()}
            >
              Salva
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function MediaCard({
  file, selected, onToggleSelect, onPreview, onCopyUrl, onRename, onDelete,
}: {
  file: MediaFileRow;
  selected: boolean;
  onToggleSelect: () => void;
  onPreview: () => void;
  onCopyUrl: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const k = kindOf(file.contentType, file.fileName);
  const Icon = iconFor(k);
  return (
    <Card
      withBorder
      padding="xs"
      radius="md"
      style={{
        outline: selected ? '2px solid var(--mantine-color-blue-filled)' : undefined,
        outlineOffset: -2,
      }}
    >
      <Card.Section style={{ position: 'relative', cursor: 'pointer' }} onClick={onPreview}>
        {k === 'image' && file.thumbUrl ? (
          <img
            src={file.thumbUrl}
            alt={file.fileName ?? ''}
            loading="lazy"
            style={{ width: '100%', aspectRatio: 1, objectFit: 'cover' }}
          />
        ) : (
          <Center h={120} bg="var(--mantine-color-default-hover)">
            <ThemeIcon size={40} variant="light" color={k === 'pdf' ? 'red' : k === 'doc' ? 'blue' : 'gray'} radius="md">
              <Icon size={22} />
            </ThemeIcon>
          </Center>
        )}
        <Checkbox
          size="xs"
          checked={selected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', top: 6, left: 6 }}
        />
      </Card.Section>
      <Tooltip label={file.fileName ?? file.id} disabled={!file.fileName}>
        <Text size="xs" mt="xs" lineClamp={1}>{file.fileName ?? file.id}</Text>
      </Tooltip>
      <Text size="xs" c="dimmed">{fmtSize(file.size)}</Text>
      <Group gap={2} mt={4} justify="flex-end">
        <Tooltip label="Anteprima">
          <ActionIcon size="sm" variant="subtle" aria-label="Anteprima" onClick={onPreview}><IconEye size={12} /></ActionIcon>
        </Tooltip>
        <Tooltip label="Copia URL">
          <ActionIcon size="sm" variant="subtle" aria-label="Copia URL" onClick={onCopyUrl}><IconCopy size={12} /></ActionIcon>
        </Tooltip>
        <Tooltip label="Rinomina">
          <ActionIcon size="sm" variant="subtle" aria-label="Rinomina" onClick={onRename}><IconPencil size={12} /></ActionIcon>
        </Tooltip>
        <Tooltip label="Elimina">
          <ActionIcon size="sm" variant="subtle" color="red" aria-label="Elimina file" onClick={onDelete}><IconTrash size={12} /></ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}
